import React, { useState } from 'react';
import './login.css';
import logo from '../../assets/wualt-logo.png';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { signIn, sendPasswordReset } from '../../services/authService';

function mapLoginError(err: any): string {
  const code: string = err?.code ?? '';
  const msg: string = err?.message ?? '';

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact your administrator.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/invalid-api-key':
      return 'Firebase configuration error. Contact support.';
    default:
      break;
  }

  if (msg.includes('user_not_found') || msg === 'User not found. Access denied.') {
    return 'No admin account found for this email. Contact your administrator.';
  }
  if (msg.includes('no_role') || msg.includes('No admin portal role') || msg.includes('not_admin') || msg.includes('Only Administrator')) {
    return 'Access denied. Only Administrator accounts can log in to this portal.';
  }
  if (msg.includes('account_inactive') || msg.includes('deactivated')) {
    return 'Your account has been deactivated. Contact your administrator.';
  }
  if (msg.startsWith('Firebase:')) {
    return 'Login failed. Please check your credentials and try again.';
  }

  return msg || 'Login failed. Please try again.';
}

function mapResetError(err: any): string {
  const code: string = err?.code ?? '';
  switch (code) {
    case 'auth/user-not-found':
      return 'No account found for this email address.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return 'Failed to send reset email. Please try again.';
  }
}

type View = 'login' | 'forgot' | 'sent';

const LoginPage: React.FC<{ onLogin?: () => void }> = ({ onLogin }) => {
  const [view, setView] = useState<View>('login');

  // Login state
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError,   setLoginError]   = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  // Forgot password state
  const [resetEmail,   setResetEmail]   = useState('');
  const [resetError,   setResetError]   = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoginLoading(true);
    try {
      await signIn(email, password);
      if (onLogin) onLogin();
    } catch (err: any) {
      setLoginError(mapLoginError(err));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetLoading(true);
    try {
      await sendPasswordReset(resetEmail.trim());
      setView('sent');
    } catch (err: any) {
      setResetError(mapResetError(err));
    } finally {
      setResetLoading(false);
    }
  };

  const goToForgot = () => {
    setResetEmail(email);
    setResetError(null);
    setView('forgot');
  };

  const goToLogin = () => {
    setResetError(null);
    setView('login');
  };

  return (
    <div className="login-root">
      <div className="login-card">
        <div className="logo-row">
          <div className="logo-circle">
            <img src={logo} alt="Wualt logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div className="brand">
            <div className="brand-name">Wualt Admin</div>
            <div className="brand-sub">Admin Portal</div>
          </div>
        </div>

        {/* ── Login view ── */}
        {view === 'login' && (
          <>
            <h1 className="title">Welcome back</h1>
            <p className="subtitle">Sign in to your admin account.</p>

            <form className="form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="label">Email address</label>
                <input
                  className={`input${loginError ? ' input-error' : ''}`}
                  type="email"
                  placeholder="admin@wualt.co"
                  required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (loginError) setLoginError(null); }}
                  disabled={loginLoading}
                />
              </div>

              <div className="form-group">
                <label className="label">Password</label>
                <div className="password-container">
                  <input
                    className={`input${loginError ? ' input-error' : ''}`}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    required
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); if (loginError) setLoginError(null); }}
                    disabled={loginLoading}
                  />
                  <button
                    type="button"
                    className="eye"
                    aria-label="toggle password"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword
                      ? <VisibilityOffOutlinedIcon style={{ width: 15, height: 15 }} />
                      : <VisibilityOutlinedIcon style={{ width: 15, height: 15 }} />
                    }
                  </button>
                </div>
              </div>

              {loginError && (
                <div className="error-banner">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="1.8" />
                    <path d="M12 8v4" stroke="#DC2626" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="12" cy="16" r="1" fill="#DC2626" />
                  </svg>
                  <span>{loginError}</span>
                </div>
              )}

              <button className="login-button" type="submit" disabled={loginLoading}>
                {loginLoading ? 'Signing in...' : 'Log in'}
              </button>

              <div className="forgot" onClick={goToForgot}>Forgot password?</div>
            </form>
          </>
        )}

        {/* ── Forgot password view ── */}
        {view === 'forgot' && (
          <>
            <h1 className="title">Reset password</h1>
            <p className="subtitle">Enter your email and we'll send you a link to reset your password.</p>

            <form className="form" onSubmit={handleForgot}>
              <div className="form-group">
                <label className="label">Email address</label>
                <input
                  className={`input${resetError ? ' input-error' : ''}`}
                  type="email"
                  placeholder="admin@wualt.co"
                  required
                  value={resetEmail}
                  onChange={(e) => { setResetEmail(e.target.value); if (resetError) setResetError(null); }}
                  disabled={resetLoading}
                  autoFocus
                />
              </div>

              {resetError && (
                <div className="error-banner">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="1.8" />
                    <path d="M12 8v4" stroke="#DC2626" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="12" cy="16" r="1" fill="#DC2626" />
                  </svg>
                  <span>{resetError}</span>
                </div>
              )}

              <button className="login-button" type="submit" disabled={resetLoading}>
                {resetLoading ? 'Sending...' : 'Send reset link'}
              </button>

              <div className="forgot" onClick={goToLogin} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <ArrowBackIcon style={{ fontSize: 13 }} />
                Back to login
              </div>
            </form>
          </>
        )}

        {/* ── Sent confirmation view ── */}
        {view === 'sent' && (
          <>
            <h1 className="title">Check your email</h1>
            <p className="subtitle">
              We've sent a password reset link to <strong>{resetEmail}</strong>. The link expires in 1 hour.
            </p>

            <div className="form" style={{ gap: 12 }}>
              <div className="success-banner">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" stroke="#059669" strokeWidth="1.8" />
                  <path d="M8 12l3 3 5-5" stroke="#059669" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Reset email sent successfully. Check your inbox and spam folder.</span>
              </div>

              <button className="login-button" type="button" onClick={() => { setView('forgot'); }}>
                Resend email
              </button>

              <div className="forgot" onClick={goToLogin} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <ArrowBackIcon style={{ fontSize: 13 }} />
                Back to login
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default LoginPage;
